import { useRef } from 'react';
import { Camera } from 'lucide-react';

const MAX_SIZE = 128;
const QUALITY = 0.6;

function optimizeImage(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width;
        let h = img.height;
        // Scale down to MAX_SIZE keeping aspect ratio
        if (w > h) {
          if (w > MAX_SIZE) { h = Math.round(h * MAX_SIZE / w); w = MAX_SIZE; }
        } else {
          if (h > MAX_SIZE) { w = Math.round(w * MAX_SIZE / h); h = MAX_SIZE; }
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', QUALITY));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function PhotoPicker({ photo, initials, color, size = 56, onPhotoChange }) {
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const optimized = await optimizeImage(file);
    onPhotoChange(optimized);
    e.target.value = '';
  };

  const px = `${size}px`;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="group relative rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        style={{ width: px, height: px }}
      >
        {photo ? (
          <img src={photo} alt="" className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-white font-bold"
            style={{
              backgroundColor: color || 'linear-gradient(135deg, var(--color-brand-500), var(--color-accent-500))',
              background: color || 'linear-gradient(135deg, var(--color-brand-500), var(--color-accent-500))',
              fontSize: `${Math.round(size * 0.35)}px`,
            }}
          >
            {initials}
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
          <Camera size={Math.round(size * 0.3)} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
